import React, {useMemo, useState} from 'react'
import {
    Autocomplete,
    Box,
    Chip,
    DialogActions,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import GroupRoundedIcon from '@mui/icons-material/GroupRounded'
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import ElectricMeterRoundedIcon from '@mui/icons-material/ElectricMeterRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import {FormProvider, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {toast} from 'sonner'
import Dialog from 'components/Display/Dialogs/Dialog'
import Button from 'components/Control/Button'
import FormInput from 'components/Control/Form/FormInput'
import FormRichText from 'components/Control/Form/FormRichText'
import EmailPreviewDialog from 'components/Modal/EmailPreviewDialog'
import {showConfirmAlert} from 'services/alert'
import {
    useGetAccountsQuery,
    useGetEmailRecipientCandidatesQuery,
    usePreviewEmailBroadcastMutation,
    useSendEmailBroadcastMutation,
} from 'store/Store'

const RECIPIENT_TYPES = [
    {value: 'user', Icon: BadgeRoundedIcon},
    {value: 'consumer', Icon: ElectricMeterRoundedIcon},
]

// Consumers only ever belong to a single account, so they don't get "all" — just pick
// specific consumers (multiple) or an entire account's worth.
const SCOPES_BY_RECIPIENT_TYPE = {
    user: [
        {value: 'multiple', Icon: GroupRoundedIcon},
        {value: 'account', Icon: ApartmentRoundedIcon},
        {value: 'all', Icon: PublicRoundedIcon},
    ],
    consumer: [
        {value: 'multiple', Icon: GroupRoundedIcon},
        {value: 'account', Icon: ApartmentRoundedIcon},
    ],
}

const DEFAULT_SCOPE_BY_RECIPIENT_TYPE = {user: 'multiple', consumer: 'multiple'}

const isBodyEmpty = (html) => !html || !html.replace(/<[^>]*>/g, '').trim()

const getRecipientLabel = (recipient) => {
    const name =
        recipient?.name || [recipient?.firstName, recipient?.lastName].filter(Boolean).join(' ')
    return name ? `${name} (${recipient.email})` : recipient?.email || ''
}

const RecipientsSection = ({
    recipientType,
    onRecipientTypeChange,
    scope,
    onScopeChange,
    selectedRecipients,
    onSelectedRecipientsChange,
    accountId,
    onAccountChange,
    search,
    onSearchChange,
    recipientOptions,
    isSearching,
    accounts,
    recipientCount,
}) => {
    const {t} = useTranslation()
    const scopeOptions = SCOPES_BY_RECIPIENT_TYPE[recipientType]

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
                {t('send_email.recipientsTitle')}
            </Typography>

            <Stack spacing={1} mb={2}>
                <ToggleButtonGroup
                    exclusive
                    value={recipientType}
                    onChange={(_e, value) => value && onRecipientTypeChange(value)}
                    size="small"
                    sx={{flexWrap: 'wrap', alignSelf: 'flex-start'}}>
                    {RECIPIENT_TYPES.map(({value, Icon}) => (
                        <ToggleButton key={value} value={value} sx={{gap: 0.75, px: 1.5}}>
                            <Icon fontSize="small" />
                            {t(`send_email.recipientType.${value}`)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <ToggleButtonGroup
                    exclusive
                    value={scope}
                    onChange={(_e, value) => value && onScopeChange(value)}
                    size="small"
                    sx={{flexWrap: 'wrap', alignSelf: 'flex-start'}}>
                    {scopeOptions.map(({value, Icon}) => (
                        <ToggleButton key={value} value={value} sx={{gap: 0.75, px: 1.5}}>
                            <Icon fontSize="small" />
                            {t(`send_email.scope.${value}`)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Stack>

            {scope === 'multiple' && (
                <Autocomplete
                    multiple
                    options={recipientOptions}
                    loading={isSearching}
                    value={selectedRecipients}
                    onChange={(_e, value) => onSelectedRecipientsChange(value)}
                    inputValue={search}
                    onInputChange={(_e, value) => onSearchChange(value)}
                    getOptionLabel={getRecipientLabel}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                {...getTagProps({index})}
                                key={option._id}
                                label={getRecipientLabel(option)}
                                size="small"
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('send_email.searchRecipients')}
                            placeholder={t('send_email.searchRecipientsPlaceholder')}
                        />
                    )}
                />
            )}

            {scope === 'account' && (
                <FormControl fullWidth>
                    <InputLabel id="send-email-account-label">
                        {t('send_email.selectAccount')}
                    </InputLabel>
                    <Select
                        labelId="send-email-account-label"
                        label={t('send_email.selectAccount')}
                        value={accountId}
                        onChange={(e) => onAccountChange(e.target.value)}>
                        {(accounts || []).map((account) => (
                            <MenuItem key={account._id} value={account._id}>
                                {account.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {scope === 'all' && (
                <Typography variant="body2" color="text.secondary">
                    {t('send_email.allUsersHint')}
                </Typography>
            )}

            <Divider sx={{my: 2}} />
            <Typography variant="body2" color="text.secondary">
                {t('send_email.recipientCount', {count: recipientCount})}
            </Typography>
        </Box>
    )
}

const SendEmailComposeDialog = ({open, onClose}) => {
    const {t} = useTranslation()
    const methods = useForm({defaultValues: {subject: '', bodyHtml: ''}})
    const {
        watch,
        reset,
        formState: {errors},
    } = methods
    const subject = watch('subject')
    const bodyHtml = watch('bodyHtml')

    const [recipientType, setRecipientType] = useState('user')
    const [scope, setScope] = useState('multiple')
    const [selectedRecipients, setSelectedRecipients] = useState([])
    const [accountId, setAccountId] = useState('')
    const [search, setSearch] = useState('')
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewHtml, setPreviewHtml] = useState('')

    const resetRecipientSelection = () => {
        setSelectedRecipients([])
        setAccountId('')
        setSearch('')
    }

    const handleScopeChange = (nextScope) => {
        setScope(nextScope)
        resetRecipientSelection()
    }

    const handleRecipientTypeChange = (nextType) => {
        setRecipientType(nextType)
        setScope(DEFAULT_SCOPE_BY_RECIPIENT_TYPE[nextType])
        resetRecipientSelection()
    }

    const {data: accountsResult} = useGetAccountsQuery({})

    // Build the query object with only real values — convertToUrlParams stringifies
    // `undefined` as the literal text "undefined", so optional keys must be omitted entirely.
    const recipientsQueryParams = useMemo(() => {
        const params = {recipientType}
        if (scope === 'multiple' && search) params.search = search
        if (scope === 'account' && accountId) params.accountId = accountId
        return params
    }, [recipientType, scope, search, accountId])

    const {data: recipientsResult, isFetching: isSearching} = useGetEmailRecipientCandidatesQuery(
        recipientsQueryParams,
        {skip: scope === 'account' && !accountId}
    )

    const [previewEmail, {isLoading: isPreviewLoading}] = usePreviewEmailBroadcastMutation()
    const [sendEmail, {isLoading: isSending}] = useSendEmailBroadcastMutation()

    const recipientCount = useMemo(() => {
        if (scope === 'multiple') return selectedRecipients.length
        return recipientsResult?.total ?? 0
    }, [scope, selectedRecipients, recipientsResult])

    const canSend =
        !isBodyEmpty(bodyHtml) &&
        Boolean(subject?.trim()) &&
        ((scope === 'multiple' && selectedRecipients.length > 0) ||
            (scope === 'account' && Boolean(accountId)) ||
            scope === 'all')

    const handlePreview = async () => {
        try {
            const {html} = await previewEmail({subject, bodyHtml}).unwrap()
            setPreviewHtml(html)
            setPreviewOpen(true)
        } catch (err) {
            toast.error(err?.data?.error || t('send_email.previewError'))
        }
    }

    const buildPayload = () => ({
        subject,
        bodyHtml,
        recipientType,
        scope,
        userIds:
            recipientType === 'user' && scope === 'multiple'
                ? selectedRecipients.map((recipient) => recipient._id)
                : undefined,
        consumerIds:
            recipientType === 'consumer' && scope === 'multiple'
                ? selectedRecipients.map((recipient) => recipient._id)
                : undefined,
        accountId: scope === 'account' ? accountId : undefined,
    })

    const doSend = () => {
        toast.promise(
            sendEmail(buildPayload())
                .unwrap()
                .then((result) => {
                    reset({subject: '', bodyHtml: ''})
                    handleRecipientTypeChange('user')
                    onClose(false)
                    return result
                }),
            {
                loading: t('loading'),
                success: (result) =>
                    t('send_email.sendSuccess', {count: result?.recipientCount ?? 0}),
                error: (err) => err?.data?.error || t('send_email.sendError'),
            }
        )
    }

    const onSendClick = (e) => {
        showConfirmAlert({
            e,
            title: t('send_email.confirmTitle'),
            text: t('send_email.confirmText', {count: recipientCount}),
            confirmButtonText: t('send_email.confirmButton'),
            onConfirm: doSend,
            disableSuccessAlert: true,
            t,
        })
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                title={t('send_email.title')}
                withFormAction={false}>
                <FormProvider {...methods}>
                    <Stack spacing={3} pb={1}>
                        <RecipientsSection
                            recipientType={recipientType}
                            onRecipientTypeChange={handleRecipientTypeChange}
                            scope={scope}
                            onScopeChange={handleScopeChange}
                            selectedRecipients={selectedRecipients}
                            onSelectedRecipientsChange={setSelectedRecipients}
                            accountId={accountId}
                            onAccountChange={setAccountId}
                            search={search}
                            onSearchChange={setSearch}
                            recipientOptions={recipientsResult?.data || []}
                            isSearching={isSearching}
                            accounts={accountsResult?.data}
                            recipientCount={recipientCount}
                        />

                        <Box>
                            <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
                                {t('send_email.composeTitle')}
                            </Typography>
                            <Stack spacing={2}>
                                <FormInput
                                    name="subject"
                                    label="subject"
                                    i18nCategory="send_email"
                                    rules={{required: 'required'}}
                                    defaultValue={subject}
                                    error={errors.subject}
                                    fullWidth
                                />
                                <FormRichText
                                    name="bodyHtml"
                                    label="content"
                                    i18nCategory="send_email"
                                    defaultValue={bodyHtml}
                                    minHeight={200}
                                />
                            </Stack>
                        </Box>
                    </Stack>
                </FormProvider>

                <DialogActions sx={{px: 0}}>
                    <Button
                        variant="outlined"
                        startIcon={<VisibilityRoundedIcon />}
                        disabled={isBodyEmpty(bodyHtml) || !subject?.trim() || isPreviewLoading}
                        onClick={handlePreview}>
                        {t('send_email.preview')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SendRoundedIcon />}
                        disabled={!canSend || isSending}
                        onClick={onSendClick}>
                        {t('send_email.send')}
                    </Button>
                </DialogActions>
            </Dialog>

            <EmailPreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                html={previewHtml}
            />
        </>
    )
}

export default SendEmailComposeDialog
